import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { loadPortalDashboard } from '../../services/supplierApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { supplierTranslations } from '../../constants/supplierTranslations';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faInfoCircle, 
  faExclamationTriangle, 
  faEye, 
  faEdit,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faSearch,
  faSpinner,
  faBoxOpen,
  faBarcode,
  faTags,
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faPercent,
  faUpload,
  faTimes,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import SupplierArticleDetailModal from './SupplierArticleDetailModal';

interface Article {
  _id: string;
  articleNumber: string;
  supplierArticleNumber?: string;
  externalArticleNumber?: string;
  supplierAssignedId?: string;
  name: string;
  brand?: string;
  gtin?: string;
  status: 'draft' | 'new' | 'requested' | 'in_progress' | 'submitted' | 'approved' | 'completed' | 'rejected' | 'needs_correction';
  completionPercentage?: number;
  updatedAt?: string;
  createdAt?: string;
  rejectionReason?: string;
}

const SupplierArticleList: React.FC = () => {
  const { masterToken } = useParams<{ masterToken: string }>();
  const { t } = useLanguage();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Helper function to normalize completion percentage
  const normalizeCompletionPercentage = (percentage?: number): number => {
    if (!percentage) return 0;
    // If the value is between 0 and 1, assume it's a decimal and convert to percentage
    if (percentage > 0 && percentage <= 1) {
      return Math.round(percentage * 100);
    }
    // If the value is already a percentage (> 1), return as is
    return Math.round(percentage);
  };

  // Helper function to get completion bar color (same as Admin Portal)
  const getCompletionBarColor = (percentage: number): string => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-yellow-500';
    if (percentage >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };
  const [sortBy, setSortBy] = useState<string>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');

  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const loadArticles = async () => {
    if (!masterToken) return;
    
    try {
      setLoading(true);
      
      const storedSessionToken = sessionStorage.getItem('supplierPortalSession');
      if (!storedSessionToken) {
        showToast('Session abgelaufen', 'error');
        return;
      }

      const data = await loadPortalDashboard(masterToken, storedSessionToken);
      if (data.articles && Array.isArray(data.articles)) {
        console.log('📦 Articles loaded:', data.articles.length);
        console.log('📦 First article data:', data.articles[0]);
        console.log('📦 Article completionPercentage values:', data.articles.map(a => ({ 
          id: a._id, 
          articleNumber: a.articleNumber,
          completionPercentage: a.completionPercentage,
          supplierAssignedId: a.supplierAssignedId 
        })));
        setArticles(data.articles);
      } else {
        setArticles([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Artikel:', error);
      showToast('Fehler beim Laden der Artikel', 'error');
    } finally {
      setLoading(false);
    }
  };

  // **DEBOUNCED SEARCH**
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    loadArticles();
  }, [masterToken]);

  // **FILTERED AND SORTED ARTICLES**
  const filteredAndSortedArticles = useMemo(() => {
    let filtered = articles.filter(article => {
      const matchesSearch = !searchTerm || 
        article.articleNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (article.supplierArticleNumber && article.supplierArticleNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (article.externalArticleNumber && article.externalArticleNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (article.supplierAssignedId && article.supplierAssignedId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        article.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (article.brand && article.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (article.gtin && article.gtin.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || article.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    // Sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy as keyof Article];
      let bValue: any = b[sortBy as keyof Article];
      
      if (sortBy === 'updatedAt' || sortBy === 'createdAt') {
        aValue = aValue ? new Date(aValue as string).getTime() : 0;
        bValue = bValue ? new Date(bValue as string).getTime() : 0;
      } else {
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      }
      
      // Handle undefined values
      if (aValue === undefined) aValue = '';
      if (bValue === undefined) bValue = '';
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [articles, searchTerm, statusFilter, sortBy, sortOrder]);

  // **SEPARATE REQUESTED AND OTHER ARTICLES**
  // Abgelehnte Artikel werden auch in "Angeforderte Artikel" angezeigt
  const requestedArticles = filteredAndSortedArticles.filter(article => 
    article.status === 'requested' || article.status === 'rejected'
  );
  const otherArticles = filteredAndSortedArticles.filter(article => 
    article.status !== 'requested' && article.status !== 'rejected'
  );

  // **PAGINATION FOR DISPLAY**
  const displayArticles = statusFilter === 'requested' ? requestedArticles : 
                         statusFilter === 'all' ? [...requestedArticles, ...otherArticles] : 
                         otherArticles;
  const totalItems = displayArticles.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedArticles = displayArticles.slice(startIndex, startIndex + itemsPerPage);

  // **STATISTICS**
  const stats = useMemo(() => {
    const stats = {
      total: articles.length,
      draft: 0,
      approved: 0,
      completed: 0,
      requested: 0,
      in_progress: 0,
      submitted: 0,
      rejected: 0,
      needs_correction: 0,
      new: 0
    };

    articles.forEach(article => {
      switch (article.status) {
        case 'draft': stats.draft++; break;
        case 'new': stats.new++; break;
        case 'approved': stats.approved++; break;
        case 'completed': stats.completed++; break;
        case 'requested': stats.requested++; break;
        case 'in_progress': stats.in_progress++; break;
        case 'submitted': stats.submitted++; break;
        case 'rejected': stats.rejected++; break;
        case 'needs_correction': stats.needs_correction++; break;
      }
    });

    return stats;
  }, [articles]);

  // **UTILITY FUNCTIONS**
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <FontAwesomeIcon icon={faSort} className="text-gray-400" />;
    return <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown} className="text-gray-600" />;
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  // **MODAL HANDLERS**
  const handleViewArticle = (articleId: string, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    const article = articles.find(a => a._id === articleId);
    if (article) {
      setSelectedArticle(article);
      setShowDetailModal(true);
    }
  };

  const handleEditArticle = (articleId: string, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    const article = articles.find(a => a._id === articleId);
    if (article) {
      setSelectedArticle(article);
      setShowDetailModal(true);
    }
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedArticle(null);
    // Refresh data in case of changes
    loadArticles();
  };

  const handleRowClick = (articleId: string) => {
    handleViewArticle(articleId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'requested': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'submitted': return 'bg-orange-100 text-orange-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'needs_correction': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return faCheckCircle;
      case 'completed': return faCheckCircle;
      case 'requested': return faClock;
      case 'in_progress': return faEdit;
      case 'submitted': return faInfoCircle;
      case 'rejected': return faTimesCircle;
      case 'needs_correction': return faExclamationTriangle;
      case 'draft': return faEye;
      default: return faInfoCircle;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Genehmigt';
      case 'completed': return 'Abgeschlossen';
      case 'requested': return 'Angefordert';
      case 'in_progress': return 'In Bearbeitung';
      case 'submitted': return 'Eingereicht';
      case 'rejected': return 'Abgelehnt';
      case 'needs_correction': return 'Korrektur erforderlich';
      case 'draft': return 'Entwurf';
      default: return status;
    }
  };

  const getStatusBadge = (status: string) => {
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusColor(status)}`}>
        {getStatusText(status)}
      </span>
    );
  };

  if (loading && !searching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Toast */}
      {toastMessage && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg z-50 transition-all ${
          toastType === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toastMessage}
        </div>
      )}

      {/* **KOMPAKTE TOOLBAR** */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          {/* **SUCHE** */}
          <div className="relative flex-1">
            <FontAwesomeIcon 
              icon={searching ? faSpinner : faSearch} 
              className={`absolute left-3 top-2.5 text-gray-400 text-sm ${searching ? 'animate-spin' : ''}`} 
            />
            <input
              type="text"
              placeholder={t(supplierTranslations.ui.searchArticles)}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
            />
          </div>
          
          {/* **FILTER UND AKTIONEN** */}
          <div className="flex gap-2">
            {/* **NEUEN ARTIKEL ERSTELLEN BUTTON** */}
            <button
              onClick={() => {
                setSelectedArticle({ _id: 'new', articleNumber: '', name: '', status: 'new' } as Article);
                setShowDetailModal(true);
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
              title={t(supplierTranslations.articles.createArticle)}
            >
              <FontAwesomeIcon icon={faPlus} className="text-xs" />
              {t(supplierTranslations.articles.createArticle)}
            </button>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white text-sm min-w-[120px]"
            >
              <option value="all">{t(supplierTranslations.ui.allStatuses)}</option>
              <option value="draft">{t(supplierTranslations.status.draft)}</option>
              <option value="new">{t({ de: 'Neu', en: 'New' })}</option>
              <option value="requested">{t(supplierTranslations.status.requested)}</option>
              <option value="in_progress">{t(supplierTranslations.status.inProgress)}</option>
              <option value="submitted">{t(supplierTranslations.status.submitted)}</option>
              <option value="approved">{t(supplierTranslations.status.approved)}</option>
              <option value="completed">{t(supplierTranslations.status.completed)}</option>
              <option value="rejected">{t(supplierTranslations.status.rejected)}</option>
              <option value="needs_correction">{t(supplierTranslations.status.needsCorrection)}</option>
            </select>
            
            <button
              onClick={resetFilters}
              className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors text-sm"
              title="Filter zurücksetzen"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      </div>

      {/* **STATISTICS CARDS** */}
      <div className="p-4 pb-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">{t(supplierTranslations.ui.total)}</p>
                <p className="text-lg font-bold text-blue-900">{stats.total}</p>
              </div>
              <div className="text-blue-500 text-sm">
                <FontAwesomeIcon icon={faBoxOpen} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">{t(supplierTranslations.status.approved)}</p>
                <p className="text-lg font-bold text-green-900">{stats.approved}</p>
              </div>
              <div className="text-green-500 text-sm">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">{t(supplierTranslations.status.inProgress)}</p>
                <p className="text-lg font-bold text-purple-900">{stats.in_progress}</p>
              </div>
              <div className="text-purple-500 text-sm">
                <FontAwesomeIcon icon={faEdit} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-600 font-medium">{t(supplierTranslations.status.requested)}</p>
                <p className="text-lg font-bold text-yellow-900">{stats.requested}</p>
              </div>
              <div className="text-yellow-500 text-sm">
                <FontAwesomeIcon icon={faClock} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-medium">{t(supplierTranslations.status.submitted)}</p>
                <p className="text-lg font-bold text-orange-900">{stats.submitted}</p>
              </div>
              <div className="text-orange-500 text-sm">
                <FontAwesomeIcon icon={faUpload} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">{t(supplierTranslations.status.draft)}</p>
                <p className="text-lg font-bold text-gray-900">{stats.draft}</p>
              </div>
              <div className="text-gray-500 text-sm">
                <FontAwesomeIcon icon={faClock} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* **CONTENT BEREICH** */}
      <div className="p-4 space-y-6">
        {totalItems === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <div className="text-gray-400 mb-4">
              <FontAwesomeIcon icon={faBoxOpen} className="text-4xl mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Keine Artikel gefunden' 
                  : 'Noch keine Artikel vorhanden'}
              </h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                {searchTerm || statusFilter !== 'all'
                  ? 'Versuchen Sie andere Suchkriterien oder Filter'
                  : 'Ihre Artikel werden hier angezeigt, sobald sie verfügbar sind'}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* **ANGEFORDERTE ARTIKEL - PRIORITY SECTION** */}
            {requestedArticles.length > 0 && statusFilter !== 'requested' && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm border border-orange-200"
              >
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 px-4 py-3 border-b border-orange-200 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-orange-900 flex items-center">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-orange-600 text-sm" />
                      {t(supplierTranslations.articles.requestedArticles)} ({requestedArticles.length})
                    </h2>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t(supplierTranslations.common.externalArtNo)}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t(supplierTranslations.common.name)}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t({ de: 'Status', en: 'Status' })}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t(supplierTranslations.common.completeness)}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t(supplierTranslations.common.requestedOn)}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t(supplierTranslations.common.actions)}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {requestedArticles.map((article) => (
                        <tr 
                          key={article._id} 
                          className="hover:bg-orange-25 cursor-pointer transition-colors hover:shadow-sm border-l-4 border-orange-400"
                          onClick={() => handleRowClick(article._id)}
                          title="Artikel bearbeiten"
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon icon={faBarcode} className="text-orange-500 text-xs" />
                              <span className="font-semibold text-orange-900">
                                {article.supplierAssignedId || article.externalArticleNumber || article.supplierArticleNumber || article.articleNumber}
                              </span>
                            </div>
                          </td>
                          
                          <td className="px-3 py-2 text-sm text-gray-900">
                            <div className="max-w-xs truncate font-medium" title={article.name}>
                              {article.name}
                            </div>
                            {article.brand && (
                              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <FontAwesomeIcon icon={faTags} className="text-xs" />
                                {article.brand}
                              </div>
                            )}
                          </td>
                          
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {getStatusBadge(article.status)}
                              {article.status === 'rejected' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Verhindert, dass der Row-Click ausgelöst wird
                                    setRejectionReason(article.rejectionReason || 'Kein Ablehnungsgrund angegeben');
                                    setShowRejectionModal(true);
                                  }}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                  title="Ablehnungsgrund anzeigen"
                                >
                                  <FontAwesomeIcon icon={faInfoCircle} className="text-sm" />
                                </button>
                              )}
                            </div>
                          </td>
                          
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full transition-all duration-500 ${getCompletionBarColor(normalizeCompletionPercentage(article.completionPercentage))}`}
                                  style={{ width: `${normalizeCompletionPercentage(article.completionPercentage)}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium text-gray-600 min-w-[2.5rem]">
                                {normalizeCompletionPercentage(article.completionPercentage)}%
                              </span>
                            </div>
                          </td>
                          
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {article.updatedAt ? new Date(article.updatedAt).toLocaleDateString('de-DE') : '—'}
                          </td>
                          
                          <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={(e) => handleEditArticle(article._id, e)}
                              className="px-2 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 transition-colors font-medium"
                              title="Artikel bearbeiten"
                            >
                              <FontAwesomeIcon icon={faEdit} className="mr-1 text-xs" />
                              Bearbeiten
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* **ANDERE ARTIKEL - STANDARD TABLE** */}
            {(otherArticles.length > 0 || statusFilter === 'requested') && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {otherArticles.length > 0 && statusFilter !== 'requested' && (
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Weitere Artikel ({otherArticles.length})
                    </h2>
                  </div>
                )}
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('supplierArticleNumber')}
                        >
                          <div className="flex items-center gap-1">
                            Externe Art.-Nr.
                            {getSortIcon('supplierArticleNumber')}
                          </div>
                        </th>
                        
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            Name
                            {getSortIcon('name')}
                          </div>
                        </th>
                        
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('status')}
                        >
                          <div className="flex items-center gap-1">
                            Status
                            {getSortIcon('status')}
                          </div>
                        </th>
                        
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('completionPercentage')}
                        >
                          <div className="flex items-center gap-1">
                            Vollständigkeit
                            {getSortIcon('completionPercentage')}
                          </div>
                        </th>
                        
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('updatedAt')}
                        >
                          <div className="flex items-center gap-1">
                            Letzte Änderung
                            {getSortIcon('updatedAt')}
                          </div>
                        </th>
                        
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Aktionen
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(statusFilter === 'requested' ? requestedArticles : otherArticles).map((article) => (
                        <tr 
                          key={article._id} 
                          className={`hover:bg-gray-50 cursor-pointer transition-colors hover:shadow-sm ${
                            article.status === 'requested' ? 'border-l-4 border-orange-400' : ''
                          }`}
                          onClick={() => handleRowClick(article._id)}
                          title="Artikel anzeigen"
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon 
                                icon={faBarcode} 
                                className={`text-xs ${
                                  article.status === 'requested' ? 'text-orange-500' : 'text-gray-400'
                                }`} 
                              />
                              {article.supplierAssignedId || article.externalArticleNumber || article.supplierArticleNumber || article.articleNumber}
                            </div>
                          </td>
                          
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="max-w-xs truncate" title={article.name}>
                              {article.name}
                            </div>
                            {article.brand && (
                              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <FontAwesomeIcon icon={faTags} className="text-xs" />
                                {article.brand}
                              </div>
                            )}
                            {article.gtin && (
                              <div className="text-xs text-gray-500 mt-1">
                                GTIN: {article.gtin}
                              </div>
                            )}
                          </td>
                          
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {getStatusBadge(article.status)}
                              {article.status === 'rejected' && (
                                <button
                                  onClick={() => {
                                    setRejectionReason(article.rejectionReason || 'Kein Ablehnungsgrund angegeben');
                                    setShowRejectionModal(true);
                                  }}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                  title="Ablehnungsgrund anzeigen"
                                >
                                  <FontAwesomeIcon icon={faInfoCircle} className="text-sm" />
                                </button>
                              )}
                            </div>
                          </td>
                          
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full transition-all duration-500 ${getCompletionBarColor(normalizeCompletionPercentage(article.completionPercentage))}`}
                                  style={{ width: `${normalizeCompletionPercentage(article.completionPercentage)}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium text-gray-600 min-w-[2.5rem]">
                                {normalizeCompletionPercentage(article.completionPercentage)}%
                              </span>
                            </div>
                          </td>
                          
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {article.updatedAt ? new Date(article.updatedAt).toLocaleDateString('de-DE') : '—'}
                          </td>
                          
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={(e) => handleViewArticle(article._id, e)}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                                title="Anzeigen"
                              >
                                <FontAwesomeIcon icon={faEye} className="text-xs" />
                              </button>
                              
                              {['requested', 'in_progress', 'needs_correction'].includes(article.status) && (
                                <button
                                  onClick={(e) => handleEditArticle(article._id, e)}
                                  className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50 transition-colors"
                                  title="Bearbeiten"
                                >
                                  <FontAwesomeIcon icon={faEdit} className="text-xs" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* **ARTIKEL DETAIL MODAL** */}
      {showDetailModal && selectedArticle && (
        <SupplierArticleDetailModal
          isOpen={showDetailModal}
          article={selectedArticle}
          onClose={handleCloseDetailModal}
          onSave={(updatedArticle) => {
            // Update the article in the local state
            setArticles(prev => prev.map(a => 
              a._id === updatedArticle._id ? updatedArticle : a
            ));
          }}
        />
      )}

      {/* **PAGINATION - STICKY BOTTOM BAR** */}
      {totalPages > 1 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="w-full px-6 py-3">
            <div className="flex items-center justify-between">
              {/* Artikel Information - Links */}
              <div>
                <p className="text-sm text-gray-700">
                  Zeige <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span> bis{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> von{' '}
                  <span className="font-medium">{totalItems}</span> Artikeln
                </p>
              </div>
              
              {/* Seitennummern - Rechts */}
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedArticle && (
        <SupplierArticleDetailModal
          isOpen={showDetailModal}
          article={selectedArticle}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedArticle(null);
          }}
        />
      )}

      {/* Rejection Reason Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRejectionModal(false)}></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FontAwesomeIcon icon={faInfoCircle} className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Grund der Ablehnung
                    </h3>
                    <div className="mt-4">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-sm text-red-700 leading-relaxed">
                          {rejectionReason}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowRejectionModal(false)}
                >
                  Verstanden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SupplierArticleList;
